import { useState } from "react";
import { toast } from "sonner";
import { useOverrideConfirmOrder } from "../../hooks/useOrders";
import type { Order } from "../../services/ordersApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface OverrideConfirmDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OverrideConfirmDialog = ({
  order,
  open,
  onOpenChange,
}: OverrideConfirmDialogProps) => {
  const [notes, setNotes] = useState("");
  const overrideMutation = useOverrideConfirmOrder();

  const handleSubmit = () => {
    if (!notes.trim()) {
      toast.error("Reason for override is required");
      return;
    }
    overrideMutation.mutate(
      { orderId: order.id, notes: notes || undefined },
      {
        onSuccess: () => {
          setNotes("");
          onOpenChange(false);
          toast.success(`Override requested for order ${order.orderNumber}`);
        },
        onError: (err: unknown) => {
          const message =
            err instanceof Error ? err.message : "Failed to confirm order";
          toast.error(message);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Payment Threshold Override</DialogTitle>
          <DialogDescription>
            Request management approval for order <strong>{order.orderNumber}</strong>
            to proceed without meeting the minimum payment threshold.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This request does not record a payment or change the outstanding balance.
              Work remains blocked until an authorized manager approves it.
            </AlertDescription>
          </Alert>

          <div className="text-sm space-y-1 text-muted-foreground">
            <p>
              Total: <strong>{formatCurrency(order.totalAmount ?? 0)}</strong>
            </p>
            <p>
              Paid: <strong>{formatCurrency(order.paidAmount ?? 0)}</strong>
            </p>
            <p>
              Balance:{" "}
              <strong className="text-red-600">
                {formatCurrency(order.balanceAmount ?? 0)}
              </strong>
            </p>
          </div>

          <Field>
            <FieldLabel htmlFor="override-notes">
              Reason for override <span className="text-red-600">*</span>
            </FieldLabel>
            <Textarea
              id="override-notes"
              placeholder="e.g. Customer paid cash on delivery arrangement"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </Field>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={overrideMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={overrideMutation.isPending}
          >
            {overrideMutation.isPending ? "Submitting…" : "Request Override"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);
